import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import ConversationsPage from "../pages/ConversationsPage";
import ChatPage from "../pages/ChatPage";

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-open widget when navigating to a chat
  useEffect(() => {
    if (location.pathname.startsWith("/chat/")) {
      setIsOpen(true);
    }
  }, [location]);

  const handleClose = () => {
    setIsOpen(false);
    // Navigate back to conversations when closing
    if (location.pathname.startsWith("/chat/")) {
      navigate("/");
    }
  };

  const handleMinimize = () => {
    setIsOpen(false);
  };

  const handleBack = () => {
    navigate("/");
  };

  return (
    <>
      {/* Chat Widget Container */}
      <div
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 transition-all duration-300 ${
          isOpen
            ? "w-[380px] h-[600px] sm:w-[420px] sm:h-[650px]"
            : "w-auto h-auto"
        }`}
      >
        {isOpen ? (
          // Open Chat Window
          <div className="w-full h-full flex flex-col bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-blue-500/30 animate-fadeIn">
            {/* Chat Header */}
            <div className="glass border-b border-blue-500/20 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">
                    BSA Assist
                  </h3>
                  <p className="text-xs text-green-400 flex items-center">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></span>
                    We're here to help
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {location.pathname.startsWith("/chat/") && (
                  <button
                    onClick={handleBack}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
                    title="Back to conversations"
                  >
                    <svg
                      className="w-4 h-4 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                )}
                <button
                  onClick={handleMinimize}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
                  title="Minimize"
                >
                  <svg
                    className="w-4 h-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 12H4"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
                  title="Close"
                >
                  <svg
                    className="w-4 h-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Chat Content Area */}
            <div className="flex-grow overflow-hidden relative">
              <Routes>
                <Route
                  path="/"
                  element={<ConversationsPage isWidget={true} />}
                />
                <Route
                  path="/chat/:id"
                  element={<ChatPage isWidget={true} />}
                />
              </Routes>
            </div>
          </div>
        ) : (
          // Closed Chat Button
          <button
            onClick={() => setIsOpen(true)}
            className="group relative animate-fadeIn"
          >
            {/* Notification Badge */}
            {hasUnread && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            )}

            {/* Chat Button */}
            <div
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 
                          flex items-center justify-center shadow-lg hover:shadow-2xl hover:scale-110 
                          transition-all duration-300 cursor-pointer"
            >
              <svg
                className="w-7 h-7 sm:w-8 sm:h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>

            {/* Hover Tooltip */}
            <div
              className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs 
                          rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 
                          pointer-events-none whitespace-nowrap"
            >
              Chat with us!
              <div
                className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 
                            border-transparent border-t-gray-800"
              ></div>
            </div>
          </button>
        )}
      </div>
    </>
  );
};

export default ChatWidget;
